import React, { useState } from 'react';
import { FileText, Globe, Database, Bot, Clock, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

/**
 * SourcesPanel - Panel dépliable affichant les sources et outils utilisés pour un message
 * 
 * Affiche :
 * - Outil IA utilisé (Perplexity, Gemini, Base de connaissances)
 * - Temps de traitement
 * - Sources citées (liens cliquables)
 * - Documents consultés
 */
const SourcesPanel = ({ 
  messageContext,
  messageId,  // ✨ NOUVEAU : ID unique du message pour éviter les conflits d'IDs
  isOpen: initialIsOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);

  // Extraire les métadonnées du message
  const {
    toolUsed = 'auto',
    processingTime = 0,
    sources = [],
    documents = [],
    modelUsed = 'gpt-4o-mini',
    searchEngine = 'perplexity_sonar'
  } = messageContext?.metadata || {};

  // Configuration des outils
  const toolConfig = {
    perplexity_sonar: {
      icon: Globe,
      label: 'Recherche Web avec Perplexity',
      color: 'purple',
      description: 'Recherche web avancée avec analyse sémantique'
    },
    perplexity_analysis: {
      icon: FileText,
      label: 'Analyse des sources avec Perplexity',
      color: 'green',
      description: 'Analyse approfondie des sources sélectionnées'
    },
    source_analysis: {
      icon: FileText,
      label: 'Analyse des sources avec Perplexity',
      color: 'green',
      description: 'Analyse approfondie des sources sélectionnées'
    },
    gemini_search: {
      icon: Globe,
      label: 'Gemini Search',
      color: 'blue',
      description: 'Recherche web avec Google Search'
    },
    knowledge_base: {
      icon: Database,
      label: 'Base de connaissances',
      color: 'green',
      description: 'Documents sauvegardés dans votre base'
    },
    gemini_flash: {
      icon: Bot,
      label: 'Gemini Flash',
      color: 'orange',
      description: 'Conversation avec modèle Gemini Flash'
    },
    auto: {
      icon: Bot,
      label: 'Auto-sélection',
      color: 'gray',
      description: 'Sélection automatique du meilleur outil'
    }
  };

  const config = toolConfig[toolUsed] || toolConfig.auto;

  // Formater le temps de traitement
  const formatProcessingTime = (seconds) => {
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    return `${seconds.toFixed(1)}s`;
  };

  // Fonction helper pour extraire le domaine d'une URL
  const extractDomain = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Rendu des sources
  const renderSources = () => {
    if (!sources || sources.length === 0) return null;

    return (
      <div className="mt-3">
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Globe size={14} />
          Sources citées
        </h4>
        <ul className="space-y-2">
          {sources.map((source, index) => {
            // Gérer les différents formats de source
            let url, title, domain;
            
            if (typeof source === 'string') {
              // Source est juste une URL
              url = source;
              title = null; // Pas de titre, on affichera seulement le domaine
              domain = extractDomain(url);
            } else if (source && typeof source === 'object') {
              // Source est un objet avec url et title
              url = source.url || '';
              title = source.title || null;
              domain = extractDomain(url);
            } else {
              // Format invalide
              return null;
            }
            
            return (
              <li 
                key={index} 
                id={messageId ? `citation-${messageId}-${index + 1}` : `citation-${index + 1}`}
                className="text-sm flex items-start gap-2 scroll-mt-4 transition-colors duration-200"
              >
                <span className="flex-shrink-0 font-mono text-blue-600 font-semibold text-sm">
                  [{index + 1}]
                </span>
                <div className="flex-1 min-w-0">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5 group"
                    title={url}
                  >
                    {title ? (
                      // Si on a un titre, afficher : Titre • domaine
                      <>
                        <span className="font-medium truncate">{title}</span>
                        <span className="text-gray-400 text-xs flex-shrink-0">•</span>
                        <span className="text-gray-500 text-xs truncate">{domain}</span>
                      </>
                    ) : (
                      // Si pas de titre, afficher seulement le domaine
                      <span className="font-medium truncate">{domain}</span>
                    )}
                    <ExternalLink size={12} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                  {source.snippet && (
                    <p className="text-gray-600 text-xs mt-1 line-clamp-2">{source.snippet}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Rendu des documents
  const renderDocuments = () => {
    if (!documents || documents.length === 0) return null;

    return (
      <div className="mt-3">
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Database size={14} />
          Documents consultés
        </h4>
        <ul className="space-y-2">
          {documents.map((doc, index) => (
            <li key={index} className="text-sm">
              <div className="text-gray-800 font-medium">{doc.title}</div>
              {doc.snippet && (
                <p className="text-gray-600 text-xs mt-1 line-clamp-2">{doc.snippet}</p>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Rendu des métriques
  const renderMetrics = () => {
    const Icon = config.icon;

    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-${config.color}-50`}>
            <Icon size={16} className={`text-${config.color}-600`} />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">{config.label}</div>
            <div className="text-xs text-gray-500">{config.description}</div>
          </div>
        </div>
        
        {processingTime > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Clock size={14} />
            <span>{formatProcessingTime(processingTime)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
      {/* Bouton pour ouvrir/fermer */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Sources et outils utilisés</span>
        </div>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* Contenu déplié */}
      {isOpen && (
        <div className="p-4 bg-white">
          {renderSources()}
          {renderDocuments()}
          
        </div>
      )}
    </div>
  );
};

export default SourcesPanel;
